import { newId, type CompleteMeasurementDto } from '@blagodar/shared';
import { api, authorizedFetch, forgetUnreachable, loadAuth, NetworkError } from '../api/client';
import { countCardOps, flushCardOps, onCardOps } from './card-ops';
import {
  countForeignOutbox,
  dequeue,
  enqueue,
  getCache,
  listPendingOutbox,
  listStuckOutbox,
  markDraftSubmitted,
  markOutboxError,
  putCache,
  syncMeasurements,
  deleteDraft,
  db,
  dropPhoto,
  listPhotos,
  markPhotoError,
  MAX_PHOTO_ATTEMPTS,
  resetOutboxAttempts,
  type CachedMeasurement,
} from './db';

/**
 * Синхронизация планшета с сервером.
 *
 * Правило простое: пользователь никогда не ждёт сеть. Замер сохраняется
 * локально и сразу считается сделанным, а доставка на сервер — фоновая
 * задача, которая переживает перезапуск приложения.
 */

export interface SyncStatus {
  online: boolean;
  syncing: boolean;
  pending: number;
  /** Отклонены сервером столько раз, что отправлять их дальше без правки бессмысленно */
  stuckCount: number;
  /** Несданное другого человека на этом планшете — уйдёт, когда он войдёт (правило 37) */
  foreign: number;
  /** Листы, снятые на холсте без сети и ещё не отправленные */
  photos: number;
  /** Расходки, сохранённые без связи и ещё не ушедшие (07.10.2026) */
  cards: number;
  /** Расходки без связи, которым сервер отказал по делу — ждут человека */
  cardsRefused: number;
  lastSyncAt: string | null;
  lastError: string | null;
}

type StatusListener = (status: SyncStatus) => void;

const listeners = new Set<StatusListener>();
let status: SyncStatus = {
  online: navigator.onLine,
  syncing: false,
  pending: 0,
  stuckCount: 0,
  foreign: 0,
  photos: 0,
  cards: 0,
  cardsRefused: 0,
  lastSyncAt: null,
  lastError: null,
};

/** Кто вошёл: отправляется только его очередь (правило 37) */
function me(): string | null {
  return loadAuth()?.user.id ?? null;
}

function setStatus(patch: Partial<SyncStatus>): void {
  status = { ...status, ...patch };
  listeners.forEach((l) => l(status));
}

export function getSyncStatus(): SyncStatus {
  return status;
}

export function onSyncStatus(listener: StatusListener): () => void {
  listeners.add(listener);
  listener(status);
  return () => listeners.delete(listener);
}

export interface BootstrapPayload {
  syncedAt: string;
  catalog: {
    categories: unknown[];
    priceItems: unknown[];
    materials: unknown[];
    leadSources: unknown[];
    brigades: unknown[];
    surveyors: unknown[];
  };
  measurements: Array<Record<string, unknown>>;
}

/** Загрузка справочников и нарядов в локальную базу. */
export async function bootstrap(): Promise<BootstrapPayload | null> {
  try {
    const data = await api.get<BootstrapPayload>('/sync/bootstrap');
    await putCache('catalog', data.catalog);
    await putCache('lastBootstrapAt', data.syncedAt);
    // Наряды ЗАМЕНЯЮТСЯ, а не дополняются: отменённый оператором иначе
    // висел бы «Запланирован», и замерщик поехал бы на снятый адрес
    await syncMeasurements(data.measurements.map(toCachedMeasurement));
    setStatus({ online: true, lastSyncAt: data.syncedAt, lastError: null });
    return data;
  } catch (error) {
    if (error instanceof NetworkError) {
      setStatus({ online: false });
      return null;
    }
    setStatus({ lastError: error instanceof Error ? error.message : 'Ошибка синхронизации' });
    return null;
  }
}

export async function getCachedCatalog(): Promise<BootstrapPayload['catalog'] | null> {
  return getCache<BootstrapPayload['catalog']>('catalog');
}

function toCachedMeasurement(m: Record<string, unknown>): CachedMeasurement {
  return {
    id: String(m.id),
    dealId: String(m.dealId),
    status: String(m.status),
    scheduledAt: String(m.scheduledAt),
    durationMin: Number(m.durationMin ?? 60),
    address: String(m.address ?? ''),
    contactPhone: (m.contactPhone as string | null) ?? null,
    objectType: (m.objectType as string | null) ?? null,
    floor: (m.floor as string | null) ?? null,
    hasElevator: (m.hasElevator as boolean | null) ?? null,
    callCenterNotes: (m.callCenterNotes as string | null) ?? null,
    surveyorNotes: (m.surveyorNotes as string | null) ?? null,
    raw: m,
    cachedAt: Date.now(),
  };
}

/**
 * Ставит завершённый замер в очередь отправки.
 * Возвращается сразу — ждать сеть замерщик не должен.
 */
export async function submitMeasurement(
  measurementId: string,
  data: CompleteMeasurementDto,
): Promise<void> {
  await enqueue(
    {
      clientMutationId: newId(),
      entityType: 'measurement',
      entityId: measurementId,
      operation: 'complete',
      payload: data,
      clientTimestamp: new Date().toISOString(),
    },
    me(),
  );
  await markDraftSubmitted(measurementId);
  setStatus({ pending: (await listPendingOutbox(me())).length });
  void flushOutbox();
}

/**
 * Отправка накопленных операций.
 *
 * Сервер отвечает по каждой операции отдельно: применена, уже была принята
 * раньше или отклонена. Из очереди убираем первые два случая — отклонённое
 * остаётся видимым, чтобы человек мог поправить данные, а не гадать,
 * почему замер «не дошёл».
 */
/**
 * Идёт ли отправка ПРЯМО СЕЙЧАС.
 *
 * Отдельной переменной, а не признаком в состоянии: между чтением
 * состояния и его записью стояли два похода в базу, и планшет,
 * проснувшийся с сетью, успевал выстрелить дважды — «сеть появилась» и
 * «вкладка стала видимой» приходят подряд. Один пакет уходил двумя
 * запросами, и сервер применял завершение замера параллельно.
 *
 * Флаг ставится ПЕРВОЙ строкой, до любого await: только так проверка и
 * установка неразделимы.
 */
let flushing = false;

export async function flushOutbox(): Promise<void> {
  if (flushing || status.syncing) return;
  flushing = true;
  try {
    await flushOutboxInner();
    // Листы с холста — после замеров: смета важнее снимка
    await flushPhotos().catch(() => undefined);
    // Расходки без связи — последними: они не ждут ни замеров, ни снимков,
    // но и отправлять их раньше смысла нет — сеть та же
    await flushCardOps().catch(() => undefined);
    await refreshCardCounts();
  } finally {
    flushing = false;
  }
}

/** Ответы, которые значат «не сейчас», а не «нет» */
export const TRANSIENT_STATUS = new Set([401, 408, 429]);

/**
 * ЛИСТЫ, СНЯТЫЕ БЕЗ СЕТИ, — ВДОГОНКУ (правило 183). Отправка листа с
 * холста была разовой: в подвале она молча не удавалась, и конструктор
 * листа не видел никогда. Сети нет или сервер лёг — ждёт следующего раза;
 * сервер отказал по делу — помечается и после пяти отказов не шлётся.
 */
export async function flushPhotos(): Promise<void> {
  const user = me();
  const queue = (await listPhotos()).filter((p) => (!p.userId || p.userId === user) && p.attempts < MAX_PHOTO_ATTEMPTS);
  for (const p of queue) {
    // Пока лист уходил, планшет могли передать другому: чужой вход — стоп
    if (me() !== user) break;
    const form = new FormData();
    form.append('file', new File([p.blob], p.name, { type: p.type || 'image/jpeg' }));
    if (p.roomId) form.append('roomId', p.roomId);
    let response: Response;
    try {
      response = await authorizedFetch(`/measurements/${p.measurementId}/attachments`, { method: 'POST', body: form }, 60_000);
    } catch {
      break;
    }
    if (response.ok) await dropPhoto(p.id);
    // Вход истёк, ожидание сорвалось, частота запросов, сервер лёг — не отказ
    // по делу: лист ждёт следующего раза, попытка не сгорает (правило 86)
    else if (TRANSIENT_STATUS.has(response.status) || response.status >= 500) break;
    else await markPhotoError(p.id, `сервер отклонил лист (${response.status})`);
  }
  const left = (await listPhotos()).filter((p) => (!p.userId || p.userId === user) && p.attempts < MAX_PHOTO_ATTEMPTS);
  setStatus({ photos: left.length });
}

async function flushOutboxInner(): Promise<void> {
  const user = me();
  const operations = await listPendingOutbox(user);
  const stuckCount = (await listStuckOutbox(user)).length;
  const foreign = await countForeignOutbox(user);
  if (operations.length === 0) {
    setStatus({ pending: 0, stuckCount, foreign });
    // Отправлять нечего — но «Оффлайн» в значке надо проверить, а не помнить
    if (!status.online) await recheckOnline();
    return;
  }

  setStatus({ syncing: true });
  try {
    const results = await api.post<
      Array<{ clientMutationId: string; status: 'applied' | 'duplicate' | 'failed'; error?: string }>
    >('/sync/push', {
      operations: operations.map((op) => ({
        clientMutationId: op.clientMutationId,
        entityType: op.entityType,
        entityId: op.entityId,
        operation: op.operation,
        payload: op.payload,
        clientTimestamp: op.clientTimestamp,
      })),
    });

    for (const result of results) {
      const op = operations.find((o) => o.clientMutationId === result.clientMutationId);
      if (result.status === 'applied' || result.status === 'duplicate') {
        await dequeue(result.clientMutationId);
        if (op?.operation === 'complete') await deleteDraft(op.entityId);
      } else {
        await markOutboxError(result.clientMutationId, result.error ?? 'Сервер отклонил операцию');
      }
    }

    const newPending = await listPendingOutbox(user);
    const newStuck = await listStuckOutbox(user);
    setStatus({
      online: true,
      pending: newPending.length,
      stuckCount: newStuck.length,
      foreign,
      lastSyncAt: new Date().toISOString(),
      lastError: newStuck.length > 0 ? (newStuck[0]!.lastError ?? 'Есть отклонённые операции') : null,
    });

    // Сервер мог пересчитать суммы — обновляем локальную копию
    await bootstrap();
  } catch (error) {
    if (error instanceof NetworkError) {
      setStatus({ online: false, pending: operations.length, stuckCount, foreign });
    } else {
      setStatus({
        lastError: error instanceof Error ? error.message : 'Ошибка отправки',
        pending: operations.length,
        stuckCount,
        foreign,
      });
    }
  } finally {
    setStatus({ syncing: false });
  }
}

/**
 * «ОФФЛАЙН» ПРОВЕРЯЕТСЯ, А НЕ ПОМНИТСЯ (12.10.2026). Значок связи гас
 * только удачной отправкой замеров или сигналом браузера «сеть вернулась».
 * У монтажника замеров нет, а Android сигнал теряет, если связь вернулась,
 * пока приложение было свёрнуто: однажды пропавший интернет оставлял
 * «Оффлайн» до перезапуска приложения при работающей связи. Монтажник так
 * и сказал: «появилось, когда не было интернета, и с тех пор не изменилось».
 *
 * Поэтому, пока горит «Оффлайн», раз в минуту, при возврате в приложение и
 * по нажатию на значок спрашивается сервер — один лёгкий `/health`. Любой
 * ответ сервера (даже ошибка) — связь есть; молчание — её нет.
 */
export async function recheckOnline(): Promise<boolean> {
  // Нажатие и опрос — настоящая попытка, а не память о недавнем молчании
  forgetUnreachable();
  try {
    await api.get('/health');
    setStatus({ online: true });
    return true;
  } catch (error) {
    const reached = !(error instanceof NetworkError);
    setStatus({ online: reached });
    return reached;
  }
}

/** Человек нажал «Повторить» на отклонённых операциях — даём им ещё один шанс. */
export async function retryStuckOutbox(): Promise<void> {
  const stuck = await listStuckOutbox(me());
  if (stuck.length === 0) return;
  await resetOutboxAttempts(stuck.map((o) => o.clientMutationId));
  setStatus({ pending: (await listPendingOutbox(me())).length, stuckCount: 0, lastError: null });
  void flushOutbox();
}

/** Отклонённое сервером — С ПРИЧИНОЙ и адресом: прочитать пальцем, а не наведением (правило 179) */
export interface RejectedOperation {
  clientMutationId: string;
  entityId: string;
  /** Адрес наряда из памяти планшета — по нему замер узнают; нет — пусто */
  address: string | null;
  error: string | null;
  attempts: number;
}

export async function listRejected(): Promise<RejectedOperation[]> {
  const stuck = await listStuckOutbox(me());
  const out: RejectedOperation[] = [];
  for (const o of stuck) {
    const cached = await db.measurements.get(o.entityId).catch(() => undefined);
    out.push({
      clientMutationId: o.clientMutationId,
      entityId: o.entityId,
      address: cached?.address || null,
      error: o.lastError,
      attempts: o.attempts,
    });
  }
  return out;
}

/** Сколько расходок ждёт связи и сколько получили отказ — в значок связи */
async function refreshCardCounts(): Promise<void> {
  const { pending, refused } = await countCardOps(me()).catch(() => ({ pending: 0, refused: 0 }));
  setStatus({ cards: pending, cardsRefused: refused });
}

let started = false;

/**
 * Запуск фоновой синхронизации: при появлении сети, при возврате в
 * приложение и раз в минуту на случай, если браузер не сообщил о сети.
 */
export function startSync(): () => void {
  if (started) return () => undefined;
  started = true;

  const onOnline = () => {
    // Сеть вернулась — короткая память о молчании сервера тут же мешает,
    // а не помогает: следующий запрос обязан пойти по-настоящему
    forgetUnreachable();
    setStatus({ online: true });
    void flushOutbox();
  };
  const onOffline = () => setStatus({ online: false });
  // Браузер говорит «сети нет» — он может и ошибаться (сигнал о возврате
  // сети теряется, пока приложение свёрнуто): проверяем сервером, и если
  // он отвечает, очередь уходит, как при живой сети
  const tick = () => {
    if (navigator.onLine) void flushOutbox();
    else
      void recheckOnline().then((ok) => {
        if (ok) void flushOutbox();
      });
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') tick();
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  document.addEventListener('visibilitychange', onVisible);

  const timer = window.setInterval(tick, 60_000);

  void listPendingOutbox(me()).then((ops) => setStatus({ pending: ops.length }));
  void refreshCardCounts();
  // Очередь расходок поменялась (сохранили без связи, убрали, ушла) — в значок
  const offCards = onCardOps((event) => {
    if (event.type === 'changed') void refreshCardCounts();
  });
  void flushOutbox();

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    document.removeEventListener('visibilitychange', onVisible);
    window.clearInterval(timer);
    offCards();
    started = false;
  };
}
